import _, { cloneDeep } from 'lodash';
import React, { useEffect, MouseEvent, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  prepareScenarioForUpdate,
  setCurrentScenario,
  setIsDeleteConfirmationOpen,
  setIsEditingSavedScenario,
  setScenarioDialogStatus,
  setTemporaryScenario,
  setUpdateScenarioDialog,
} from 'spw-redux/modules/calculators/actions';
import BlackLinkIcon from 'spw-static/images/black-link.svg';
import MutedNotLinkedIcon from 'spw-static/images/not-linked.svg';
import { IAppState } from 'spw-redux/reducer';
import { IProject } from 'spw-redux/modules/projects/types';
import * as GA from 'spw-shared/analytics';
import {
  Column,
  Row,
  SPWCancelButton,
  SPWDeleteButton,
  SPWMainButton,
  SPWOutlineBlackButton,
} from 'spw-shared/globalComponents';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';
import { GROWTH_CALCULATORS, TOTAL_PROGRAM_TYPE } from 'spw-constants';
import { setCurrentFloor } from 'spw-redux/modules/projects/actions';
import { IUser } from 'spw-redux/modules/user/types';
import { Box, Button, Popover } from '@mui/material';
import { isDepartmentsTypeScenario } from 'spw-shared/utils';
import { isScenarioLinked } from 'spw-components/growth-calculator/total-program-utils';
import {
  selectCurrentScenario,
  selectTotalPrograms,
} from 'spw-redux/modules/calculators/selectors';
import { useHistory } from 'react-router';
import { getScenarioListLocation } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import {
  ButtonColumn,
  HeaderBuildingColumn,
  ScenarioName,
  TitleName,
  LeftArrow,
  CustomEditIcon,
  LinkedButton,
  PopoverHeader,
  PopoverContent,
  PopoverInnerWrapper,
} from './styles';
import ProgramScenario, {
  PlainProgramScenario,
} from '../../../models/Scenario';

interface IHeader {
  isEditingSavedScenario: boolean;
  scenarioFloorId?: null | number;
  currentProject: IProject | null;
  currentScenario: PlainProgramScenario;
  temporaryScenario: PlainProgramScenario;
  projectType: string;
  user: IUser;
  totalPrograms: PlainProgramScenario[];
}

const Header: React.FC<IHeader> = ({
  isEditingSavedScenario,
  currentProject,
  currentScenario,
  temporaryScenario,
  projectType,
  user,
  totalPrograms,
}) => {
  const dispatch = useDispatch();
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);

  const [anchorEl, setAnchorEl] = useState(null);
  const history = useHistory();

  const handleClick = (event: React.SyntheticEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  let title = 'Building';
  if (currentScenario?.calculatorType === GROWTH_CALCULATORS.macroProgramming) {
    title = 'Macro Programming';
  } else if (isDepartmentsTypeScenario(currentScenario)) {
    title = 'Departments';
  } else if (currentScenario?.floorId) {
    const floor = _.find(currentProject.buildings[0].floors, {
      id: currentScenario.floorId,
    });

    title = floor?.name || 'Untitled Floor';
  }

  const backToList = () => {
    const location = getScenarioListLocation(currentProject, currentScenario);

    if (
      [
        GROWTH_CALCULATORS.floorHeadcountSeatcount,
        GROWTH_CALCULATORS.floorUsf,
      ].includes(currentScenario.calculatorType)
    ) {
      const floor = _.find(currentProject.buildings[0].floors, {
        id: currentScenario.floorId,
      });

      dispatch(setCurrentFloor(floor));
    }

    history.replace(location);
  };

  useEffect(() => {
    return () => {
      dispatch(setTemporaryScenario(null));
    };
  }, [dispatch]);

  const isTotalProgram = currentScenario.calculatorType === TOTAL_PROGRAM_TYPE;
  const isDepartment =
    isDepartmentsTypeScenario(currentScenario) && !isTotalProgram;
  let isLinked = false;
  if (isDepartment) {
    isLinked = isScenarioLinked(currentScenario?.id, totalPrograms);
  }
  let linkedEle;
  if (isDepartment) {
    linkedEle = (
      <ButtonColumn>
        {isLinked ? (
          <>
            <LinkedButton onClick={handleClick}>
              <img src={BlackLinkIcon} alt="linked" />
              <div>LINKED</div>
            </LinkedButton>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <PopoverInnerWrapper>
                <PopoverHeader>Tool Tip</PopoverHeader>
                <PopoverContent>
                  <div>
                    When a dept scenario is linked to a Total Program Scenario,
                    the following values cannot be changed:
                  </div>
                  <ul style={{ marginTop: '0', paddingLeft: '33px' }}>
                    <li>Departments</li>
                    <li>Target Year</li>
                    <li>Growth Years</li>
                  </ul>
                  <div>
                    To edit these values, select SAVE AS to create a copy or
                    delink the dept scenario from all Total Program Scenarios.
                  </div>
                </PopoverContent>
              </PopoverInnerWrapper>
            </Popover>
          </>
        ) : (
          <Button disabled>
            <img src={MutedNotLinkedIcon} alt="not linked" />
            <div
              style={{
                marginLeft: '8px',
                fontFamily: 'Roboto',
                fontSize: '14px',
                letterSpacing: '0.25px',
                color: '#707070',
              }}
            >
              NOT LINKED
            </div>
          </Button>
        )}
      </ButtonColumn>
    );
  }

  const disableSaving = !ProgramScenario.isValid(currentScenario);

  return (
    <Row>
      <HeaderBuildingColumn>
        <Row>
          <TitleName onClick={backToList}>{title}</TitleName>
          <LeftArrow>
            <img alt="arrow-back" src="/images/arrow-back.svg" />
          </LeftArrow>
          <ScenarioName>{currentScenario?.name}</ScenarioName>
          <Box display="flex" alignItems="center">
            <CustomEditIcon
              disabled={currentUserIsViewOnly}
              key="remove"
              onClick={
                currentUserIsViewOnly
                  ? undefined
                  : (e: MouseEvent<SVGSVGElement>) => {
                      e.preventDefault();
                      dispatch(setUpdateScenarioDialog(true));
                      dispatch(setScenarioDialogStatus(true));
                    }
              }
            />
          </Box>
        </Row>
      </HeaderBuildingColumn>
      <Column>
        {!isEditingSavedScenario && !isTotalProgram ? (
          <Row>
            {linkedEle && linkedEle}
            <SPWMainButton
              disabled={currentUserIsViewOnly}
              onClick={() => {
                dispatch(setIsEditingSavedScenario(true));
                dispatch(setTemporaryScenario(cloneDeep(currentScenario)));
              }}
            >
              EDIT
            </SPWMainButton>
          </Row>
        ) : (
          <Row>
            {linkedEle && linkedEle}
            {!isTotalProgram && (
              <ButtonColumn>
                <SPWMainButton
                  disabled={disableSaving || currentUserIsViewOnly}
                  onClick={() => {
                    let bldgOrFloor = 'BLDG';
                    let subCategory = 'Building Program Scenario';
                    if (currentScenario?.floorId) {
                      bldgOrFloor = 'FL';
                      subCategory = 'Floor Program Scenario';
                    }
                    GA.event({
                      category: projectType as GA.GACategory,
                      subCategory,
                      action: GA.tag(`${bldgOrFloor}_SCEN_SAVE`, projectType),
                      label: JSON.stringify({
                        projectType,
                        userId: user.id,
                      }),
                    });
                    dispatch(prepareScenarioForUpdate());
                  }}
                >
                  SAVE
                </SPWMainButton>
              </ButtonColumn>
            )}
            <ButtonColumn>
              <SPWCancelButton
                disabled={disableSaving || currentUserIsViewOnly}
                onClick={() => dispatch(setScenarioDialogStatus(true, true))}
              >
                SAVE AS
              </SPWCancelButton>
            </ButtonColumn>
            <ButtonColumn>
              <SPWOutlineBlackButton
                onClick={() => {
                  dispatch(setCurrentScenario(temporaryScenario));
                  dispatch(setTemporaryScenario(null));
                  dispatch(setIsEditingSavedScenario(false));
                  if (isTotalProgram) {
                    const location = getScenarioListLocation(
                      currentProject,
                      currentScenario
                    );
                    history.replace(location);
                  }
                }}
              >
                {isTotalProgram ? 'CLOSE' : 'CANCEL'}
              </SPWOutlineBlackButton>
            </ButtonColumn>
            <ButtonColumn>
              <SPWDeleteButton
                disabled={currentUserIsViewOnly}
                onClick={() => dispatch(setIsDeleteConfirmationOpen(true))}
              >
                DELETE
              </SPWDeleteButton>
            </ButtonColumn>
          </Row>
        )}
      </Column>
    </Row>
  );
};

const mapStateToProps = (state: IAppState) => ({
  scenarioFloorId: state.calculatorReducer.scenarioFloorId,
  isEditingSavedScenario: state.calculatorReducer.isEditingSavedScenario,
  currentProject: state.projectsReducer.currentProject,
  currentScenario: selectCurrentScenario(state),
  temporaryScenario: state.calculatorReducer.temporaryScenario,
  projectType: state.projectsReducer.currentProjectType,
  user: state.userReducer.user,
  totalPrograms: selectTotalPrograms(state),
});

export default connect(mapStateToProps)(Header);
